import { reactive } from 'vue'

import { ColumnPreferencesService, type ColumnLayout, type ColumnPreferenceOut } from '@/api'
import { extractErrorMessage } from '@/utils/apiError'

export type ColumnPreferenceLoadStatus = 'loaded' | 'missing' | 'invalid' | 'unavailable'
export interface ColumnPreferenceScope<Preference> {
  preference: Preference | null
  status: ColumnPreferenceLoadStatus
  warning?: string | null
}
export interface ColumnPreferenceWriteResult<Preference> {
  persisted: boolean
  preference: Preference | null
}
interface StoredScope<Preference> extends ColumnPreferenceScope<Preference> {
  serverPreference: Preference | null
  hydrated: boolean
  revision: number
  change: number
  pending: boolean
  conflicted: boolean
}

export function uniqueStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) return null
  return [...new Set(value)]
}

export function parseColumnWidths(value: unknown): Record<string, number> | null {
  if (value === undefined) return {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const entries = Object.entries(value)
  if (
    entries.some(([key, width]) => !key || !Number.isInteger(width) || width < 24 || width > 5000)
  )
    return null
  return Object.fromEntries(entries)
}

export function createColumnPreferenceStorage<Preference extends ColumnLayout>(
  storagePrefix: string,
  parse: (raw: string) => Preference | null,
  clone: (preference: Preference | null) => Preference | null,
  tableKind: 'assay' | 'workbook' | 'source_files',
) {
  const scopes = reactive<Record<string, StoredScope<Preference>>>({})
  const reads = new Map<string, Promise<void>>()
  const writes = new Map<string, Promise<void>>()
  let generation = 0
  const keyFor = (userId: string, projectId: number) => `${encodeURIComponent(userId)}:${projectId}`

  function scopeFor(userId: string, projectId: number): StoredScope<Preference> {
    const key = keyFor(userId, projectId)
    scopes[key] ??= {
      preference: null,
      serverPreference: null,
      status: 'missing',
      hydrated: false,
      revision: 0,
      change: 0,
      pending: false,
      conflicted: false,
      warning: null,
    }
    return scopes[key] as StoredScope<Preference>
  }

  function load(userId: string, projectId: number): ColumnPreferenceScope<Preference> {
    const scope = scopeFor(userId, projectId)
    return { preference: clone(scope.preference), status: scope.status, warning: scope.warning }
  }

  function removeLegacy(userId: string, projectId: number): void {
    try {
      localStorage.removeItem(`${storagePrefix}:${keyFor(userId, projectId)}`)
    } catch {
      /* Account storage remains available. */
    }
  }

  function legacyPreference(userId: string, projectId: number): Preference | null {
    try {
      const raw = localStorage.getItem(`${storagePrefix}:${keyFor(userId, projectId)}`)
      return raw ? parse(raw) : null
    } catch {
      return null
    }
  }

  function warning(scope: StoredScope<Preference>, error: unknown): void {
    scope.status = 'unavailable'
    const message = scope.pending
      ? 'Your layout is applied in this session, but could not be saved to your account.'
      : 'Your saved layout could not be loaded. Try again to restore it.'
    scope.warning = `${message} ${extractErrorMessage(error)}`
  }

  async function importLegacy(
    result: ColumnPreferenceOut,
    userId: string,
    projectId: number,
    epoch: number,
  ): Promise<ColumnPreferenceOut> {
    const legacy =
      result.revision === 0 && !scopeFor(userId, projectId).pending
        ? legacyPreference(userId, projectId)
        : null
    if (!legacy || epoch !== generation) return result
    try {
      return await ColumnPreferencesService.putColumnPreferenceProjectsProjectIdColumnPreferencesTableKindPut(
        { projectId, tableKind, requestBody: { layout: legacy, expected_revision: 0 } },
      )
    } catch (error) {
      if ((error as { status?: number }).status !== 409 || epoch !== generation) throw error
      return ColumnPreferencesService.getColumnPreferenceProjectsProjectIdColumnPreferencesTableKindGet(
        { projectId, tableKind },
      )
    }
  }

  async function hydrate(userId: string, projectId: number): Promise<void> {
    const key = keyFor(userId, projectId)
    const scope = scopeFor(userId, projectId)
    if (scope.hydrated) return
    if (reads.has(key)) return reads.get(key)
    const epoch = generation
    const reading = (async () => {
      try {
        const saved =
          await ColumnPreferencesService.getColumnPreferenceProjectsProjectIdColumnPreferencesTableKindGet(
            { projectId, tableKind },
          )
        if (epoch !== generation) return
        const result = await importLegacy(saved, userId, projectId, epoch)
        if (epoch !== generation) return
        const preference = result.layout ? parse(JSON.stringify(result.layout)) : null
        scope.serverPreference = clone(preference)
        scope.revision = result.revision
        scope.hydrated = true
        if (result.layout && !preference && !scope.pending) {
          scope.status = 'invalid'
          scope.warning = 'The saved layout is invalid. Reset the layout and try again.'
          return
        }
        if (!scope.pending) scope.preference = preference
        scope.status = scope.preference ? 'loaded' : 'missing'
        if (!scope.pending) scope.warning = null
        if (result.revision > 0) removeLegacy(userId, projectId)
      } catch (error) {
        if (epoch === generation) warning(scope, error)
      } finally {
        if (epoch === generation) reads.delete(key)
      }
    })()
    reads.set(key, reading)
    return reading
  }

  async function flush(userId: string, projectId: number): Promise<void> {
    const key = keyFor(userId, projectId)
    if (writes.has(key)) return writes.get(key)
    const scope = scopeFor(userId, projectId)
    const epoch = generation
    const writing = (async () => {
      while (scope.pending && scope.hydrated && !scope.conflicted && epoch === generation) {
        const change = scope.change
        try {
          const result =
            await ColumnPreferencesService.putColumnPreferenceProjectsProjectIdColumnPreferencesTableKindPut(
              {
                projectId,
                tableKind,
                requestBody: { layout: clone(scope.preference), expected_revision: scope.revision },
              },
            )
          if (epoch !== generation) return
          scope.serverPreference = result.layout ? parse(JSON.stringify(result.layout)) : null
          scope.revision = result.revision
          if (scope.change === change) scope.pending = false
          scope.status = scope.preference ? 'loaded' : 'missing'
          scope.warning = null
          removeLegacy(userId, projectId)
        } catch (error) {
          if (epoch !== generation) return
          warning(scope, error)
          if ((error as { status?: number }).status === 409) {
            scope.hydrated = false
            scope.conflicted = true
          }
          return
        }
      }
    })().finally(() => {
      if (epoch === generation) writes.delete(key)
    })
    writes.set(key, writing)
    return writing
  }

  async function update(
    userId: string,
    projectId: number,
    transform: (current: Preference | null, server: Preference | null) => Preference | null,
  ): Promise<ColumnPreferenceWriteResult<Preference>> {
    const epoch = generation
    if (!scopeFor(userId, projectId).conflicted) await hydrate(userId, projectId)
    if (epoch !== generation) return { persisted: false, preference: null }
    const scope = scopeFor(userId, projectId)
    scope.preference = clone(transform(clone(scope.preference), clone(scope.serverPreference)))
    scope.change += 1
    scope.pending = true
    if (!scope.hydrated && !scope.conflicted) {
      scope.status = 'unavailable'
      scope.warning =
        'Your layout is applied in this session, but could not be saved to your account. Retry to save your current layout.'
    }
    await flush(userId, projectId)
    return {
      persisted: epoch === generation && !scope.pending,
      preference: clone(scope.preference),
    }
  }

  function save(
    userId: string,
    projectId: number,
    preference: Preference | null,
  ): Promise<ColumnPreferenceWriteResult<Preference>> {
    return update(userId, projectId, () => preference)
  }

  async function retry(
    userId: string,
    projectId: number,
    reconcile?: (local: Preference | null, remote: Preference | null) => Preference | null,
  ): Promise<void> {
    const epoch = generation
    await hydrate(userId, projectId)
    if (epoch !== generation) return
    const scope = scopeFor(userId, projectId)
    if (!scope.hydrated) return
    if (reconcile && scope.pending) {
      scope.preference = clone(reconcile(clone(scope.preference), clone(scope.serverPreference)))
      scope.change += 1
    }
    scope.conflicted = false
    await flush(userId, projectId)
  }

  function clear(): void {
    generation += 1
    reads.clear()
    writes.clear()
    for (const key of Object.keys(scopes)) delete scopes[key]
  }

  return {
    load,
    hydrate,
    save,
    update,
    reset: (userId: string, projectId: number) => save(userId, projectId, null),
    retry,
    clear,
  }
}
